import { TypesContextesAutorisesEnum } from '@/features/auth/context-app'
import { isAdherentOuMandatGestion } from '@/features/domaine/regles-akto/shared'
import { LevelRegleEnum, type ModelDemande } from '@/features/domaine/types/opco'
import type { DemandeStore } from '@/features/interface/demande/shared'
import service from '@/features/services/demande/DemandeAllService'
import type {
  SearchCodeDiplome,
  SearchCommune,
  SearchEtablissementAdherent,
  SearchEtablissementPrestataire,
  SearchFormacode,
  SearchIdcc,
  SearchNsf,
  SearchRncp,
  SearchRs,
} from '@/features/services/demande/DemandeAllService.types'
import { noop } from '@/features/shared/tools'
import {
  navigateTo,
  type NavigateToDemandeExtranet,
  type NavigateToQueryDemandeFormation,
  type NavigateToQueryDR,
  type NavigateToQueryDRF,
  type NavigateToQueryProforma,
} from '@/router/navigateTo'
import { useCancelNavigation } from '@/features/interface/composables'
import { type IDialog, useDialog } from '@/features/interface/dialog'
import { useEventBus } from '@/features/application/app-events/AppEvents'
import type { AppNotifEvent } from '@/features/application/app-events'
import { useRouter } from 'vue-router'

export const NOTIF_SAUVEGARDE_EFFECTUEE: AppNotifEvent = {
  name: 'AppNotif',
  arg: {
    message: 'Votre document a été sauvegardé',
    titre: 'Sauvegarde effectuée',
    type: 'success',
    duration: 'short',
  },
}

export type BuildDemandeActions = typeof buildDemandeActions
export type DemandeActions = ReturnType<BuildDemandeActions>

export function buildDemandeActions<TDemande extends ModelDemande>(store: DemandeStore<TDemande, any>) {
  const dialog: IDialog = useDialog()
  const eventBus = useEventBus()
  const router = useRouter()

  // Cancel navigation si model modifié
  useCancelNavigation({
    canLeave: () => !store.estModifié || store.readonly,
  })

  // Sauvegarde et transmission
  const save = async (level: LevelRegleEnum = LevelRegleEnum.Technique) => {
    // Sortie si en erreur
    if (store.meta.$estErreur && store.meta.$level! <= level) {
      await dialog.showDialog({
        message: "Veuillez corriger les erreurs et renouveler l'opération.",
        type: 'Error',
        title: 'La saisie est invalide',
      })
      store.dirtifyReject(level)
      return
    }

    if (!(await store.saveData())) return

    if (level < LevelRegleEnum.Transmettre) {
      eventBus?.dispatch(NOTIF_SAUVEGARDE_EFFECTUEE)
      return
    }

    // Afficher confirmation demande transmise
    const estSousMandatGestion = store.model?.dispositif === 'CAPP' && store.model.estSousMandatGestion === true
    const etatAdherentOuMandatGestion = isAdherentOuMandatGestion(store.typeContextCourant, estSousMandatGestion)
    if (!(await store.submitData(etatAdherentOuMandatGestion))) return

    if (!etatAdherentOuMandatGestion) {
      await dialog.showDialog({
        type: 'Ok',
        title: "Votre demande a été transmise à l'adhérent",
        isHtml: true,
        message: `
      <div style="text-align:center;">
        Merci pour votre pré-saisie.<br/>
        Un mail vient d'être adressé à « l'adhérent » pour l'informer de votre pré-saisie et
        l'inviter à vérifier, compléter et transmettre la demande à AKTO.<br/>
        À réception de la demande, le dossier sera traité dans les meilleurs délais. <br/>
        L'équipe d’AKTO
      </div>
    `,
      })
    } else {
      await dialog.showDialog({
        type: 'Ok',
        title: 'Votre demande a été transmise à AKTO',
        message: '',
      })
    }
  }

  // Suppression du brouillon
  const deleteBrouillon = async (location: NavigateToDemandeExtranet) => {
    const result = await dialog.showDialog({
      message: 'Confirmez-vous la suppression définitive du brouillon ?',
      type: 'YesNo',
      title: 'Supprimer un brouillon',
    })
    if (result !== 'Yes') return

    await service.deleteDemande(location)
    store.$reset()

    // Si l'utilisateur a navigué jusqu'ici (entrée précédente dans l'historique),
    // on revient en arrière — sinon on redirige vers la recherche par défaut.
    if (window.history.state?.back != null) {
      router.back()
    } else {
      navigateTo(buildRechercheFromDemande(location)).catch(noop)
    }
  }

  // Confirmation ou Refus
  const confirm = async (accept: boolean) => {
    if (!accept) {
      const result = await dialog.showDialog({
        message: `Confirmez-vous le refus de la demande?`,
        type: 'YesNo',
        title: 'Refuser la demande',
      })
      if (result === 'No') return
    }

    await store.confirmData(accept)
    if (!accept) {
      await dialog.showDialog({
        message: 'La demande a été refusée',
        type: 'Ok',
        title: 'Confirmation refus',
      })

      // const context = TypesContextesAutorisesEnum[store.typeContextCourant]
      // router.push(`/${context}/brouillons`.toLowerCase()).catch(noop)

      navigateTo({ to: 'recherche', domaine: 'formations', module: 'demande' }).catch(noop)
    }
  }

  // Impressions Document
  /*
  const downloadDocumentTemplate = async () => {
    if (!typeDocumentTemplate.value)
      return

    const time = new Date().getTime()
    const dispositif = store.model?.typeDemande === 'Proforma' ? store.model?.financeur : store.model?.dispositif
    const blob = await store.downloadDocumentTemplate(typeDocumentTemplate.value!)
    const reference = store.model?.typeDemande === 'Proforma' ? store.model?.reference : store.model?.id
    await displayFile(
      blob,
      `${TypeDocumentTemplateNomFichier[typeDocumentTemplate.value!]}_${dispositif}_${reference}_${time}.pdf`,
    )

    if (store.model?.typeDemande === 'Proforma') {
      const blob = await store.downloadDocumentTemplate('ProFormaAnnexe')
      await displayFile(blob, `${TypeDocumentTemplateNomFichier['ProFormaAnnexe']}_${reference}_${time}.xlsx`)
    }
  }
  async function showDialogDemandeModifie() {
    const dialog: IDialog = useDialog()
    const type: string =
      store.model?.typeDemande === 'Proforma'
        ? 'de la proforma'
        : store.model?.dispositif === 'FPE'
          ? 'du contrat type ou de la lettre de mission.'
          : 'du CERFA'

    await dialog.showDialog({
      isHtml: true,
      message: `<div style="text-align:center;">La demande a été mise à jour. <br/>Merci d\'enregistrer votre demande avant de procéder à la génération ${type}</div>`,
      type: 'Ok',
    })
  }
  async function displayFile(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    try {
      a.href = url
      a.target = '_blank'

      a.download = filename
      document.body.appendChild(a)
    } finally {
      a.click()
      a.remove()

      // Libère la mémoire après utilisation
      setTimeout(() => window.URL.revokeObjectURL(url), 0)
    }
  }
  */

  /* Recherche de CFA */
  const searchPrestataire = buildSearchPrestataire(false)

  const searchPrestataireAll = buildSearchPrestataire(true)

  function buildSearchPrestataire(tous: boolean) {
    return async (searchStr: string) => {
      if (/^\d{9,14}$/.test(searchStr)) {
        const modeRechercheTous = tous && store.typeContextCourant === TypesContextesAutorisesEnum.Prestataire
        const search = modeRechercheTous
          ? ({
              search: searchStr,
              dispositif: store.model!.dispositif,
              modeRecherche: 'RechercherTousPrestataires',
            } as SearchEtablissementPrestataire)
          : ({
              search: searchStr,
              dispositif: store.model!.dispositif,
            } as SearchEtablissementAdherent)

        return await service.searchEtablissementPrestataire(search, {
          sablier: false,
          dialogMode: 'silent',
        })
      }
      return []
    }
  }

  /* Recherche de RNCP */
  const searchRNCP = (search: string | SearchRncp) =>
    service.searchRncp(search, {
      sablier: false,
      dialogMode: 'silent',
      autoCancel: true,
    })

  /* Recherche de NSF */
  const searchNsf = (search: string | SearchNsf) =>
    service.searchNsf(search, {
      sablier: false,
      dialogMode: 'silent',
      autoCancel: true,
    })

  /* Recherche de Code Diplome */
  const searchCodeDiplome = (search: string | SearchCodeDiplome) =>
    service.searchCodeDiplome(search, {
      sablier: false,
      dialogMode: 'silent',
      autoCancel: true,
    })

  /* Recherche de formacode */
  const searchFormacode = (search: string | SearchFormacode) =>
    service.searchFormacode(search, {
      sablier: false,
      dialogMode: 'silent',
      autoCancel: true,
    })

  /* Recherche Répertoire spécifique (RS) */
  const searchRs = (search: string | SearchRs) =>
    service.searchRs(search, {
      sablier: false,
      dialogMode: 'silent',
      autoCancel: true,
    })

  /* Recherche de IDCC */
  const searchIdcc = (search: string | SearchIdcc) =>
    service.searchIdcc(search, {
      sablier: false,
      dialogMode: 'silent',
      autoCancel: true,
    })

  /* Recherche de Commune */
  const searchCommune = (search: string | SearchCommune) =>
    service.searchCommune(search, {
      sablier: false,
      dialogMode: 'silent',
      autoCancel: true,
    })

  return {
    searchPrestataire,
    searchPrestataireAll,
    searchRNCP,
    searchNsf,
    searchCodeDiplome,
    searchFormacode,
    searchRs,
    searchIdcc,
    searchCommune,
    save,
    confirm,
    deleteBrouillon,
    getRncp: service.getRncpSilent,
  }
}

// Construit la location de recherche adaptée au formulaire courant (utilisée
// pour rediriger après suppression d'un brouillon quand il n'y a pas d'historique).
function buildRechercheFromDemande(
  location: NavigateToDemandeExtranet,
): NavigateToQueryDemandeFormation | NavigateToQueryDRF | NavigateToQueryDR | NavigateToQueryProforma {
  switch (location.domaine) {
    case 'formations':
      return {
        to: 'recherche',
        domaine: 'formations',
        module: 'demande',
      }
    case 'drf':
      return {
        to: 'recherche',
        domaine: 'drf',
        module: 'demande',
      }
    case 'tt':
      return {
        to: 'recherche',
        domaine: 'tt',
        module: 'demande',
      }
    case 'dr':
      return location.module === 'proforma'
        ? {
            to: 'recherche',
            domaine: 'dr',
            module: 'proforma',
          }
        : {
            to: 'recherche',
            domaine: 'dr',
            module: 'demande',
          }
  }
}
